import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Avatar,
    Badge,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Collapse,
    Drawer,
    Grid,
    IconButton,
    InputBase,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import TuneIcon from '@mui/icons-material/Tune';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddCommentOutlinedIcon from '@mui/icons-material/AddCommentOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';
import { useSnackbar } from 'notistack';
import ChatMarkdown from './ChatMarkdown';
import HistorialConversacionesIa from './HistorialConversacionesIa';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminRole } from '../../utils/ubicacionUsuarioRoles';
import ConsultaIaLlmConfigDialog from './ConsultaIaLlmConfigDialog';
import {
    StreamNoIniciadoError,
    analizarConsulta,
    analizarConsultaStream,
    eliminarConversacion as eliminarConversacionApi,
    enviarFeedbackMensaje,
    listarConversaciones,
    mensajeDeError,
    migrarHistorialLocal,
    obtenerConversacion,
    renombrarConversacion as renombrarConversacionApi,
} from '../../services/consultasIaService';
import type {
    ConsultaAnalizarRequest,
    ConversacionIaResumen,
    FiltrosConsultaIa,
    MensajeConversacionIa,
    ModuloConsultaIa,
    RespuestaAnalizarIa,
} from '../../types/consultasIa';
import SearchableSelect from '../SearchableSelect';
import {
    CONFIG_MODULOS_IA,
    camposActivos,
    construirPayload,
    etiquetaAlcance,
    filtrosIniciales,
    valorVisible,
    type ConfigModuloIa,
} from './consultaIaModulos';

type Filtros = FiltrosConsultaIa;

interface Mensaje extends MensajeConversacionIa {
    /** Respuesta completa recibida sin streaming: se muestra con efecto de escritura. */
    animar?: boolean;
    /** Respuesta que se está recibiendo por streaming. */
    enCurso?: boolean;
}

const ANCHO_HISTORIAL = 280;

const esCancelacion = (error: unknown): boolean => {
    const e = error as { name?: string; code?: string };
    return e?.name === 'AbortError' || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
};

export interface ConsultaIaChatProps {
    titulo: string;
    modulo: ModuloConsultaIa;
}

const MAX_ANCHO_CONVERSACION = 1080;

const aIsoLocal = (d: Date): string => {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
};

const aFechaVisible = (iso: string): string => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const rangoMesesAtras = (meses: number): { desde: string; hasta: string } => {
    const hoy = new Date();
    const desde = new Date();
    desde.setMonth(hoy.getMonth() - meses);
    return { desde: aIsoLocal(desde), hasta: aIsoLocal(hoy) };
};

const validarRango = (desde: string, hasta: string): string => {
    if (!desde || !hasta) return '';
    const inicio = new Date(desde);
    const fin = new Date(hasta);
    if (inicio > fin) return 'La fecha desde no puede ser posterior a la fecha hasta';
    const diffMeses = (fin.getFullYear() - inicio.getFullYear()) * 12 + (fin.getMonth() - inicio.getMonth());
    if (diffMeses > 3) return 'El rango de fechas no puede ser mayor a 3 meses';
    return '';
};

const nuevoId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const parpadeo = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
`;

const rebote = keyframes`
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
`;

const IndicadorEscribiendo: React.FC = () => (
    <Stack direction="row" spacing={0.75} sx={{ py: 1.25 }}>
        {[0, 1, 2].map((i) => (
            <Box
                key={i}
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: '#1976d2',
                    animation: `${rebote} 1.4s infinite ease-in-out`,
                    animationDelay: `${i * 0.16}s`,
                }}
            />
        ))}
    </Stack>
);

interface TextoEscribiendoProps {
    texto: string;
    onProgreso: () => void;
    onFin: () => void;
}

const TextoEscribiendo: React.FC<TextoEscribiendoProps> = ({ texto, onProgreso, onFin }) => {
    const [visibles, setVisibles] = useState(0);

    useEffect(() => {
        const paso = Math.max(2, Math.ceil(texto.length / 250));
        const id = window.setInterval(() => {
            setVisibles((prev) => Math.min(texto.length, prev + paso));
        }, 16);
        return () => window.clearInterval(id);
    }, [texto]);

    useEffect(() => {
        onProgreso();
        if (visibles >= texto.length) onFin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visibles]);

    return (
        <Box sx={{ position: 'relative' }}>
            <ChatMarkdown texto={texto.slice(0, visibles)} mostrarAcciones={false} />
            <CursorEscritura />
        </Box>
    );
};

const CursorEscritura: React.FC = () => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            width: 8,
            height: 16,
            bgcolor: '#1976d2',
            borderRadius: 0.5,
            verticalAlign: 'text-bottom',
            animation: `${parpadeo} 1s step-end infinite`,
        }}
    />
);

const etiquetaRango = (filtros: Filtros) =>
    `${aFechaVisible(filtros.fechaDesde ?? '') || '…'} – ${aFechaVisible(filtros.fechaHasta ?? '') || '…'}`;

const ChipsFiltros: React.FC<{ config: ConfigModuloIa; filtros: Filtros }> = ({ config, filtros }) => {
    const alcance = etiquetaAlcance(config, filtros);
    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            {(filtros.fechaDesde || filtros.fechaHasta) && (
                <Chip
                    size="small"
                    icon={<CalendarMonthIcon />}
                    label={etiquetaRango(filtros)}
                    variant="outlined"
                    sx={{ bgcolor: '#ffffff' }}
                />
            )}
            {alcance && <Chip size="small" label={alcance} variant="outlined" sx={{ bgcolor: '#ffffff' }} />}
            {camposActivos(config, filtros)
                .filter((c) => (filtros[c.clave] ?? '').trim())
                .map((c) => (
                    <Chip
                        key={c.clave}
                        size="small"
                        label={`${c.etiqueta}: ${valorVisible(c, filtros[c.clave].trim())}`}
                        variant="outlined"
                        sx={{ bgcolor: '#ffffff' }}
                    />
                ))}
        </Stack>
    );
};

const ConsultaIaChat: React.FC<ConsultaIaChatProps> = ({ titulo, modulo }) => {
    const { enqueueSnackbar } = useSnackbar();
    const config = CONFIG_MODULOS_IA[modulo];

    const [filtros, setFiltros] = useState<Filtros>(() => {
        const { desde, hasta } = rangoMesesAtras(3);
        return filtrosIniciales(config, desde, hasta);
    });
    const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
    const [pregunta, setPregunta] = useState('');
    const [mensajes, setMensajes] = useState<Mensaje[]>([]);
    const [loading, setLoading] = useState(false);

    const { user, tenantId } = useAuth();
    const puedeConfigurarLlm = isAdminRole(user?.roles);
    const [configLlmAbierta, setConfigLlmAbierta] = useState(false);
    const theme = useTheme();
    const esEscritorio = useMediaQuery(theme.breakpoints.up('md'));
    const [historialAbierto, setHistorialAbierto] = useState(true);
    const [historialMovilAbierto, setHistorialMovilAbierto] = useState(false);
    const [conversaciones, setConversaciones] = useState<ConversacionIaResumen[]>([]);
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [cargandoConversacion, setCargandoConversacion] = useState(false);
    const [conversacionId, setConversacionId] = useState<string | null>(null);
    const conversacionIdRef = useRef<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const scrollRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    const fijarConversacionActual = (id: string | null) => {
        conversacionIdRef.current = id;
        setConversacionId(id);
    };

    const recargarConversaciones = useCallback(async () => {
        try {
            setConversaciones(await listarConversaciones(modulo));
        } catch (err) {
            console.error('Error al cargar el historial de consultas IA:', err);
            enqueueSnackbar(mensajeDeError(err, 'No se pudo cargar el historial de conversaciones'), {
                variant: 'error',
            });
        } finally {
            setCargandoHistorial(false);
        }
    }, [enqueueSnackbar, modulo]);

    useEffect(() => {
        let cancelado = false;
        abortRef.current?.abort();
        conversacionIdRef.current = null;
        setConversacionId(null);
        setMensajes([]);
        setCargandoHistorial(true);

        (async () => {
            try {
                const importadas = await migrarHistorialLocal(modulo, tenantId, user?.username);
                if (importadas > 0 && !cancelado) {
                    enqueueSnackbar(
                        `Se importaron ${importadas} conversaciones que estaban guardadas en este navegador`,
                        {
                            variant: 'info',
                        },
                    );
                }
            } catch (err) {
                console.error('No se pudo importar el historial local de consultas IA:', err);
            }
            if (!cancelado) await recargarConversaciones();
        })();

        return () => {
            cancelado = true;
        };
    }, [modulo, tenantId, user?.username, recargarConversaciones, enqueueSnackbar]);

    useEffect(() => () => abortRef.current?.abort(), []);

    const errorFechas = useMemo(
        () => validarRango(filtros.fechaDesde ?? '', filtros.fechaHasta ?? ''),
        [filtros.fechaDesde, filtros.fechaHasta],
    );

    const campos = camposActivos(config, filtros);
    const alcanceActual = etiquetaAlcance(config, filtros);
    const filtrosAdicionalesActivos = campos.filter((c) => (filtros[c.clave] ?? '').trim()).length;

    const actualizarFiltro = (campo: string, valor: string) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

    const aplicarRangoRapido = (meses: number) => {
        const { desde, hasta } = rangoMesesAtras(meses);
        setFiltros((prev) => ({ ...prev, fechaDesde: desde, fechaHasta: hasta }));
    };

    const aplicarMesActual = () => {
        const hoy = new Date();
        const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        setFiltros((prev) => ({ ...prev, fechaDesde: aIsoLocal(inicio), fechaHasta: aIsoLocal(hoy) }));
    };

    const limpiarFiltrosAdicionales = () =>
        setFiltros((prev) => {
            const siguiente = { ...prev };
            campos.forEach((c) => delete siguiente[c.clave]);
            return siguiente;
        });

    const scrollAlFinal = useCallback((forzar = false) => {
        const el = scrollRef.current;
        if (!el) return;
        const cercaDelFinal = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
        if (forzar || cercaDelFinal) el.scrollTop = el.scrollHeight;
    }, []);

    useEffect(() => {
        scrollAlFinal(true);
    }, [mensajes.length, loading, scrollAlFinal]);

    const enviar = async (texto: string, filtrosEnvio: Filtros) => {
        const preguntaLimpia = texto.trim();
        if (!preguntaLimpia || loading) return;

        const error = validarRango(filtrosEnvio.fechaDesde ?? '', filtrosEnvio.fechaHasta ?? '');
        if (error) {
            setFiltrosAbiertos(true);
            enqueueSnackbar(error, { variant: 'warning' });
            return;
        }

        const idUsuario = nuevoId();
        const idAsistente = nuevoId();
        const controller = new AbortController();
        abortRef.current = controller;

        setMensajes((prev) => [
            ...prev.map((m) => ({ ...m, animar: false })),
            {
                id: idUsuario,
                rol: 'usuario',
                texto: preguntaLimpia,
                fecha: new Date().toISOString(),
                filtros: { ...filtrosEnvio },
            },
        ]);
        setPregunta('');
        setLoading(true);

        const actualizarMensaje = (id: string, cambios: Partial<Mensaje>) =>
            setMensajes((prev) => prev.map((m) => (m.id === id ? { ...m, ...cambios } : m)));

        const agregarDelta = (delta: string) => {
            setMensajes((prev) =>
                prev.some((m) => m.id === idAsistente)
                    ? prev.map((m) => (m.id === idAsistente ? { ...m, texto: m.texto + delta } : m))
                    : [
                          ...prev,
                          {
                              id: idAsistente,
                              rol: 'asistente',
                              texto: delta,
                              fecha: new Date().toISOString(),
                              enCurso: true,
                          },
                      ],
            );
            window.requestAnimationFrame(() => scrollAlFinal());
        };

        const payload: ConsultaAnalizarRequest = {
            ...construirPayload(modulo, config, filtrosEnvio),
            conversacionId: conversacionIdRef.current,
            pregunta: preguntaLimpia,
        };

        const aplicarIdsServidor = (r: Pick<RespuestaAnalizarIa, 'conversacionId' | 'mensajeUsuarioId'>) => {
            if (r.conversacionId && r.conversacionId !== conversacionIdRef.current) {
                fijarConversacionActual(r.conversacionId);
            }
            if (r.mensajeUsuarioId) actualizarMensaje(idUsuario, { servidorId: r.mensajeUsuarioId });
        };

        let conStreaming = true;
        try {
            let resultado: RespuestaAnalizarIa;
            try {
                resultado = await analizarConsultaStream(payload, {
                    signal: controller.signal,
                    onInicio: aplicarIdsServidor,
                    onDelta: agregarDelta,
                });
            } catch (err) {
                if (!(err instanceof StreamNoIniciadoError)) throw err;
                console.warn('Streaming no disponible, se usa /analizar:', err.message);
                conStreaming = false;
                resultado = await analizarConsulta(payload);
            }

            aplicarIdsServidor(resultado);
            const textoFinal = resultado.respuesta?.trim() || 'No obtuve una respuesta para esta consulta.';

            if (conStreaming) {
                setMensajes((prev) =>
                    prev.some((m) => m.id === idAsistente)
                        ? prev.map((m) =>
                              m.id === idAsistente
                                  ? { ...m, enCurso: false, servidorId: resultado.mensajeAsistenteId }
                                  : m,
                          )
                        : [
                              ...prev,
                              {
                                  id: idAsistente,
                                  rol: 'asistente',
                                  texto: textoFinal,
                                  fecha: new Date().toISOString(),
                                  servidorId: resultado.mensajeAsistenteId,
                              },
                          ],
                );
            } else {
                setMensajes((prev) => [
                    ...prev,
                    {
                        id: idAsistente,
                        rol: 'asistente',
                        texto: textoFinal,
                        fecha: new Date().toISOString(),
                        servidorId: resultado.mensajeAsistenteId,
                        animar: true,
                    },
                ]);
            }
        } catch (err: unknown) {
            if (esCancelacion(err)) {
                setMensajes((prev) =>
                    prev.some((m) => m.id === idAsistente)
                        ? prev.map((m) => (m.id === idAsistente ? { ...m, enCurso: false } : m))
                        : [
                              ...prev,
                              {
                                  id: idAsistente,
                                  rol: 'asistente',
                                  texto: '_Respuesta detenida._',
                                  fecha: new Date().toISOString(),
                              },
                          ],
                );
            } else {
                console.error('Error al procesar la consulta:', err);
                setMensajes((prev) => [
                    ...prev.map((m) => (m.id === idAsistente ? { ...m, enCurso: false } : m)),
                    {
                        id: nuevoId(),
                        rol: 'asistente',
                        texto: mensajeDeError(err),
                        fecha: new Date().toISOString(),
                        error: true,
                        preguntaOrigen: { texto: preguntaLimpia, filtros: { ...filtrosEnvio } },
                    },
                ]);
            }
        } finally {
            if (abortRef.current === controller) abortRef.current = null;
            setLoading(false);
            inputRef.current?.focus();
            void recargarConversaciones();
        }
    };

    const detener = () => abortRef.current?.abort();

    const calificar = async (mensaje: Mensaje, valor: 1 | -1) => {
        if (!mensaje.servidorId || mensaje.feedback === valor) return;
        const anterior = mensaje.feedback;
        setMensajes((prev) => prev.map((m) => (m.id === mensaje.id ? { ...m, feedback: valor } : m)));
        try {
            await enviarFeedbackMensaje(mensaje.servidorId, valor);
            enqueueSnackbar('¡Gracias por tu opinión!', { variant: 'success' });
        } catch (err) {
            setMensajes((prev) => prev.map((m) => (m.id === mensaje.id ? { ...m, feedback: anterior } : m)));
            enqueueSnackbar(mensajeDeError(err, 'No se pudo registrar tu opinión'), { variant: 'error' });
        }
    };

    const handleSubmit = (e?: React.FormEvent) => {
        e?.preventDefault();
        void enviar(pregunta, filtros);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const finalizarAnimacion = useCallback((id: string) => {
        setMensajes((prev) => prev.map((m) => (m.id === id && m.animar ? { ...m, animar: false } : m)));
    }, []);

    const copiar = async (texto: string) => {
        try {
            await navigator.clipboard.writeText(texto);
            enqueueSnackbar('Respuesta copiada al portapapeles', { variant: 'success' });
        } catch {
            enqueueSnackbar('No se pudo copiar la respuesta', { variant: 'error' });
        }
    };

    const nuevaConversacion = () => {
        fijarConversacionActual(null);
        setMensajes([]);
        setPregunta('');
        setHistorialMovilAbierto(false);
        inputRef.current?.focus();
    };

    const abrirConversacion = async (id: string) => {
        if (loading || cargandoConversacion || id === conversacionIdRef.current) {
            setHistorialMovilAbierto(false);
            return;
        }
        setCargandoConversacion(true);
        setHistorialMovilAbierto(false);
        try {
            const conversacion = await obtenerConversacion(id);
            if (!conversacion) {
                enqueueSnackbar('No se encontró la conversación', { variant: 'warning' });
                void recargarConversaciones();
                return;
            }
            fijarConversacionActual(conversacion.id);
            setMensajes(conversacion.mensajes);
            const ultimosFiltros = [...conversacion.mensajes].reverse().find((m) => m.filtros)?.filtros;
            if (ultimosFiltros) {
                setFiltros((prev) => ({
                    ...filtrosIniciales(config, prev.fechaDesde ?? '', prev.fechaHasta ?? ''),
                    ...ultimosFiltros,
                }));
            }
            setPregunta('');
        } catch (err) {
            console.error('Error al abrir la conversación:', err);
            enqueueSnackbar(mensajeDeError(err, 'No se pudo abrir la conversación'), { variant: 'error' });
        } finally {
            setCargandoConversacion(false);
        }
    };

    const renombrarConversacion = async (id: string, nuevoTitulo: string) => {
        const anteriores = conversaciones;
        setConversaciones((prev) => prev.map((c) => (c.id === id ? { ...c, titulo: nuevoTitulo } : c)));
        try {
            await renombrarConversacionApi(id, nuevoTitulo);
        } catch (err) {
            setConversaciones(anteriores);
            enqueueSnackbar(mensajeDeError(err, 'No se pudo renombrar la conversación'), { variant: 'error' });
        }
    };

    const eliminarConversacion = async (id: string) => {
        try {
            await eliminarConversacionApi(id);
            if (id === conversacionIdRef.current) nuevaConversacion();
            setConversaciones((prev) => prev.filter((c) => c.id !== id));
            enqueueSnackbar('Conversación eliminada', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(mensajeDeError(err, 'No se pudo eliminar la conversación'), { variant: 'error' });
        }
    };

    const alternarHistorial = () => {
        if (esEscritorio) setHistorialAbierto((v) => !v);
        else setHistorialMovilAbierto(true);
    };

    const historialVisible = esEscritorio && historialAbierto;

    const panelHistorial = (
        <HistorialConversacionesIa
            conversaciones={conversaciones}
            seleccionadaId={conversacionId}
            cargando={cargandoHistorial}
            deshabilitado={loading || cargandoConversacion}
            onSeleccionar={(id) => void abrirConversacion(id)}
            onNueva={nuevaConversacion}
            onRenombrar={(id, t) => void renombrarConversacion(id, t)}
            onEliminar={(id) => void eliminarConversacion(id)}
        />
    );

    const hayConversacion = mensajes.length > 0 || loading || cargandoConversacion;
    const puedeEnviar = !!pregunta.trim() && !loading && !cargandoConversacion && !errorFechas;
    const esperandoPrimerTexto = loading && !mensajes.some((m) => m.enCurso);

    const estiloRespuesta = {
        color: 'text.primary',
        wordBreak: 'break-word',
        fontSize: '1.02rem',
        '& .MuiTypography-root': { fontSize: 'inherit' },
    } as const;

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: '#fafafa',
            '&:hover': { bgcolor: '#f5f5f5' },
            '&.Mui-focused': { bgcolor: '#ffffff' },
        },
    };

    const avatarAsistente = (
        <Avatar sx={{ width: 36, height: 36, bgcolor: '#1976d2', flexShrink: 0 }}>
            <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        </Avatar>
    );

    return (
        <Card
            sx={{
                bgcolor: '#ffffff',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                display: 'flex',
                height: 'calc(100vh - 150px)',
                minHeight: 640,
            }}
        >
            {esEscritorio ? (
                <Box
                    sx={{
                        width: historialVisible ? ANCHO_HISTORIAL : 0,
                        flexShrink: 0,
                        overflow: 'hidden',
                        borderRight: historialVisible ? '1px solid #e0e0e0' : 'none',
                        transition: 'width 0.2s ease',
                    }}
                >
                    <Box sx={{ width: ANCHO_HISTORIAL, height: '100%' }}>{panelHistorial}</Box>
                </Box>
            ) : (
                <Drawer
                    open={historialMovilAbierto}
                    onClose={() => setHistorialMovilAbierto(false)}
                    PaperProps={{ sx: { width: ANCHO_HISTORIAL } }}
                >
                    {panelHistorial}
                </Drawer>
            )}

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1.5}
                    sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid #e0e0e0' }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        <Tooltip title={historialVisible ? 'Ocultar historial' : 'Ver historial'}>
                            <IconButton onClick={alternarHistorial} color={historialVisible ? 'primary' : 'default'}>
                                {historialVisible ? <MenuOpenIcon /> : <HistoryIcon />}
                            </IconButton>
                        </Tooltip>
                        <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                    {titulo}
                                </Typography>
                                {puedeConfigurarLlm && (
                                    <Tooltip title="Configurar el modelo de IA">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            aria-label="Configurar el modelo de IA"
                                            onClick={() => setConfigLlmAbierta(true)}
                                        >
                                            <SettingsIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                                Hacé preguntas en lenguaje natural sobre {config.objeto}.
                            </Typography>
                        </Box>
                    </Stack>
                    {!historialVisible && (
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddCommentOutlinedIcon />}
                            onClick={nuevaConversacion}
                            disabled={!hayConversacion || loading}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500, flexShrink: 0 }}
                        >
                            Nueva conversación
                        </Button>
                    )}
                </Stack>

                <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                    <Box
                        sx={{ maxWidth: MAX_ANCHO_CONVERSACION, mx: 'auto', height: hayConversacion ? 'auto' : '100%' }}
                    >
                        {cargandoConversacion ? (
                            <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', py: 8 }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Cargando conversación...
                                </Typography>
                            </Stack>
                        ) : !hayConversacion ? (
                            <Stack
                                alignItems="center"
                                justifyContent="center"
                                spacing={3}
                                sx={{ height: '100%', py: 4 }}
                            >
                                <Avatar sx={{ width: 56, height: 56, bgcolor: '#1976d2' }}>
                                    <AutoAwesomeIcon />
                                </Avatar>
                                <Typography variant="h5" sx={{ fontWeight: 600, textAlign: 'center' }}>
                                    ¿En qué puedo ayudarte con {config.objeto}?
                                </Typography>
                                <Grid container spacing={1.5} sx={{ maxWidth: 720 }}>
                                    {config.sugerencias.map((s) => (
                                        <Grid item xs={12} sm={6} key={s}>
                                            <Box
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => void enviar(s, filtros)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') void enviar(s, filtros);
                                                }}
                                                sx={{
                                                    p: 1.75,
                                                    height: '100%',
                                                    border: '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                    cursor: 'pointer',
                                                    transition: 'all 0.15s',
                                                    '&:hover': { bgcolor: '#f5f9ff', borderColor: '#1976d2' },
                                                }}
                                            >
                                                <Typography variant="body2">{s}</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                        ) : (
                            <Stack spacing={3}>
                                {mensajes.map((m) =>
                                    m.rol === 'usuario' ? (
                                        <Stack key={m.id} direction="row" spacing={1.5} justifyContent="flex-end">
                                            <Stack spacing={0.75} alignItems="flex-end" sx={{ maxWidth: '80%' }}>
                                                <Box
                                                    sx={{
                                                        bgcolor: '#1976d2',
                                                        color: '#ffffff',
                                                        px: 2.25,
                                                        py: 1.5,
                                                        borderRadius: '20px 20px 4px 20px',
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                        fontSize: '1.02rem',
                                                        lineHeight: 1.6,
                                                    }}
                                                >
                                                    {m.texto}
                                                </Box>
                                                {m.filtros && <ChipsFiltros config={config} filtros={m.filtros} />}
                                            </Stack>
                                            <Avatar
                                                sx={{ width: 36, height: 36, bgcolor: '#e3f2fd', color: '#1976d2' }}
                                            >
                                                <PersonIcon sx={{ fontSize: 22 }} />
                                            </Avatar>
                                        </Stack>
                                    ) : (
                                        <Stack key={m.id} direction="row" spacing={1.5} alignItems="flex-start">
                                            {avatarAsistente}
                                            <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                                {m.error ? (
                                                    <Alert
                                                        severity="error"
                                                        sx={{ borderRadius: 2 }}
                                                        action={
                                                            m.preguntaOrigen && (
                                                                <Button
                                                                    color="inherit"
                                                                    size="small"
                                                                    startIcon={<RefreshIcon />}
                                                                    disabled={loading}
                                                                    onClick={() =>
                                                                        m.preguntaOrigen &&
                                                                        void enviar(
                                                                            m.preguntaOrigen.texto,
                                                                            m.preguntaOrigen.filtros,
                                                                        )
                                                                    }
                                                                    sx={{ textTransform: 'none' }}
                                                                >
                                                                    Reintentar
                                                                </Button>
                                                            )
                                                        }
                                                    >
                                                        {m.texto}
                                                    </Alert>
                                                ) : m.enCurso ? (
                                                    <Box sx={estiloRespuesta}>
                                                        <ChatMarkdown texto={m.texto} mostrarAcciones={false} />
                                                        <CursorEscritura />
                                                    </Box>
                                                ) : m.animar ? (
                                                    <Box sx={estiloRespuesta}>
                                                        <TextoEscribiendo
                                                            texto={m.texto}
                                                            onProgreso={() => scrollAlFinal()}
                                                            onFin={() => finalizarAnimacion(m.id)}
                                                        />
                                                    </Box>
                                                ) : (
                                                    <>
                                                        <Box sx={estiloRespuesta}>
                                                            <ChatMarkdown texto={m.texto} />
                                                        </Box>
                                                        <Stack direction="row" spacing={0.25} sx={{ mt: 0.5 }}>
                                                            <Tooltip title="Copiar respuesta">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => void copiar(m.texto)}
                                                                    sx={{ color: 'text.secondary' }}
                                                                >
                                                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                            {m.servidorId && (
                                                                <>
                                                                    <Tooltip title="Buena respuesta">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => void calificar(m, 1)}
                                                                            sx={{
                                                                                color:
                                                                                    m.feedback === 1
                                                                                        ? '#1976d2'
                                                                                        : 'text.secondary',
                                                                            }}
                                                                        >
                                                                            {m.feedback === 1 ? (
                                                                                <ThumbUpAltIcon sx={{ fontSize: 16 }} />
                                                                            ) : (
                                                                                <ThumbUpAltOutlinedIcon
                                                                                    sx={{ fontSize: 16 }}
                                                                                />
                                                                            )}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                    <Tooltip title="Mala respuesta">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => void calificar(m, -1)}
                                                                            sx={{
                                                                                color:
                                                                                    m.feedback === -1
                                                                                        ? 'error.main'
                                                                                        : 'text.secondary',
                                                                            }}
                                                                        >
                                                                            {m.feedback === -1 ? (
                                                                                <ThumbDownAltIcon
                                                                                    sx={{ fontSize: 16 }}
                                                                                />
                                                                            ) : (
                                                                                <ThumbDownAltOutlinedIcon
                                                                                    sx={{ fontSize: 16 }}
                                                                                />
                                                                            )}
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </>
                                                            )}
                                                        </Stack>
                                                    </>
                                                )}
                                            </Box>
                                        </Stack>
                                    ),
                                )}
                                {esperandoPrimerTexto && (
                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                        {avatarAsistente}
                                        <IndicadorEscribiendo />
                                    </Stack>
                                )}
                            </Stack>
                        )}
                    </Box>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2, pt: 1 }}>
                    <Box sx={{ maxWidth: MAX_ANCHO_CONVERSACION, mx: 'auto' }}>
                        <Collapse in={filtrosAbiertos} unmountOnExit>
                            <Box
                                sx={{ border: '1px solid #e0e0e0', borderRadius: 3, p: 2, mb: 1.5, bgcolor: '#ffffff' }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1.5 }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                        Filtros de la consulta
                                    </Typography>
                                    <IconButton size="small" onClick={() => setFiltrosAbiertos(false)}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                            <Chip label="Este mes" size="small" onClick={aplicarMesActual} />
                                            <Chip
                                                label="Último mes"
                                                size="small"
                                                onClick={() => aplicarRangoRapido(1)}
                                            />
                                            <Chip
                                                label="Últimos 3 meses"
                                                size="small"
                                                onClick={() => aplicarRangoRapido(3)}
                                            />
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Fecha Desde"
                                            type="date"
                                            value={filtros.fechaDesde ?? ''}
                                            onChange={(e) => actualizarFiltro('fechaDesde', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errorFechas}
                                            sx={textFieldSx}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Fecha Hasta"
                                            type="date"
                                            value={filtros.fechaHasta ?? ''}
                                            onChange={(e) => actualizarFiltro('fechaHasta', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            error={!!errorFechas}
                                            sx={textFieldSx}
                                        />
                                    </Grid>
                                    {errorFechas && (
                                        <Grid item xs={12}>
                                            <Alert severity="error" sx={{ borderRadius: 2, py: 0 }}>
                                                {errorFechas}
                                            </Alert>
                                        </Grid>
                                    )}
                                    {config.alcance && (
                                        <Grid item xs={12}>
                                            <SearchableSelect<string>
                                                label={config.alcance.etiqueta}
                                                size="small"
                                                value={
                                                    filtros[config.alcance.clave] || config.alcance.opciones[0].valor
                                                }
                                                onChange={(v) =>
                                                    config.alcance &&
                                                    actualizarFiltro(
                                                        config.alcance.clave,
                                                        v ?? config.alcance.opciones[0].valor,
                                                    )
                                                }
                                                options={config.alcance.opciones.map((o) => ({
                                                    value: o.valor,
                                                    label: o.etiqueta,
                                                }))}
                                                sx={textFieldSx}
                                            />
                                        </Grid>
                                    )}
                                    {campos.map((c) => (
                                        <Grid item xs={12} sm={campos.length >= 3 ? 4 : 6} key={c.clave}>
                                            {c.tipo === 'select' ? (
                                                <SearchableSelect<string>
                                                    label={c.etiqueta}
                                                    size="small"
                                                    allowEmpty
                                                    emptyOptionLabel={c.etiquetaVacia ?? 'Todos'}
                                                    value={filtros[c.clave] || null}
                                                    onChange={(v) => actualizarFiltro(c.clave, v ?? '')}
                                                    options={c.opciones ?? []}
                                                    sx={textFieldSx}
                                                />
                                            ) : (
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={c.etiqueta}
                                                    value={filtros[c.clave] ?? ''}
                                                    onChange={(e) =>
                                                        actualizarFiltro(
                                                            c.clave,
                                                            c.tipo === 'numero'
                                                                ? e.target.value.replace(/\D/g, '')
                                                                : e.target.value,
                                                        )
                                                    }
                                                    inputProps={
                                                        c.tipo === 'numero' ? { inputMode: 'numeric' } : undefined
                                                    }
                                                    helperText={c.ayuda}
                                                    sx={textFieldSx}
                                                />
                                            )}
                                        </Grid>
                                    ))}
                                    {filtrosAdicionalesActivos > 0 && (
                                        <Grid item xs={12}>
                                            <Button
                                                size="small"
                                                onClick={limpiarFiltrosAdicionales}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Limpiar filtros
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            </Box>
                        </Collapse>

                        <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                border: '1px solid',
                                borderColor: errorFechas ? 'error.main' : '#d0d7de',
                                borderRadius: 4,
                                bgcolor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                px: 1.5,
                                pt: 1.25,
                                pb: 1,
                                transition: 'border-color 0.15s',
                                '&:focus-within': { borderColor: errorFechas ? 'error.main' : '#1976d2' },
                            }}
                        >
                            <InputBase
                                inputRef={inputRef}
                                fullWidth
                                multiline
                                maxRows={6}
                                placeholder={`Preguntá lo que quieras sobre ${config.objeto}...`}
                                value={pregunta}
                                onChange={(e) => setPregunta(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={loading}
                                minRows={2}
                                sx={{ px: 0.5, fontSize: '1.02rem', lineHeight: 1.6 }}
                            />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mt: 1 }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={0.75}
                                    useFlexGap
                                    flexWrap="wrap"
                                    alignItems="center"
                                    sx={{ minWidth: 0 }}
                                >
                                    <Tooltip title={filtrosAbiertos ? 'Ocultar filtros' : 'Filtros de la consulta'}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setFiltrosAbiertos((v) => !v)}
                                            color={filtrosAbiertos ? 'primary' : 'default'}
                                        >
                                            <Badge
                                                color="primary"
                                                badgeContent={filtrosAdicionalesActivos}
                                                invisible={!filtrosAdicionalesActivos}
                                            >
                                                <TuneIcon fontSize="small" />
                                            </Badge>
                                        </IconButton>
                                    </Tooltip>
                                    <Chip
                                        size="small"
                                        icon={<CalendarMonthIcon />}
                                        label={etiquetaRango(filtros)}
                                        onClick={() => setFiltrosAbiertos(true)}
                                        color={errorFechas ? 'error' : 'default'}
                                        variant="outlined"
                                    />
                                    {alcanceActual && (
                                        <Chip
                                            size="small"
                                            label={alcanceActual}
                                            onClick={() => setFiltrosAbiertos(true)}
                                            variant="outlined"
                                        />
                                    )}
                                    {campos
                                        .filter((c) => (filtros[c.clave] ?? '').trim())
                                        .map((c) => (
                                            <Chip
                                                key={c.clave}
                                                size="small"
                                                label={`${c.etiqueta}: ${valorVisible(c, filtros[c.clave].trim())}`}
                                                onDelete={() => actualizarFiltro(c.clave, '')}
                                                variant="outlined"
                                            />
                                        ))}
                                </Stack>
                                {loading ? (
                                    <Tooltip title="Detener respuesta">
                                        <IconButton
                                            type="button"
                                            onClick={detener}
                                            sx={{
                                                bgcolor: '#1976d2',
                                                color: '#ffffff',
                                                width: 36,
                                                height: 36,
                                                '&:hover': { bgcolor: '#1565c0' },
                                            }}
                                        >
                                            <StopRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Enviar (Enter)">
                                        <span>
                                            <IconButton
                                                type="submit"
                                                disabled={!puedeEnviar}
                                                sx={{
                                                    bgcolor: '#1976d2',
                                                    color: '#ffffff',
                                                    width: 36,
                                                    height: 36,
                                                    '&:hover': { bgcolor: '#1565c0' },
                                                    '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
                                                }}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center', mt: 1 }}
                        >
                            La respuesta se basa en una sola página de datos según los filtros. Shift + Enter para salto
                            de línea.
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {puedeConfigurarLlm && (
                <ConsultaIaLlmConfigDialog open={configLlmAbierta} onClose={() => setConfigLlmAbierta(false)} />
            )}
        </Card>
    );
};

export default ConsultaIaChat;
